import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Lock, ArrowRight, Users, Star, CheckCircle } from 'lucide-react';
import BottomTabNavigation from '../../components/navigation/BottomTabNavigation';
import UserProfileHeader from '../../components/navigation/UserProfileHeader';

const AppointmentsUpgradePrompt = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <UserProfileHeader />
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 pb-24">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md">

          {/* Lock icon */}
          <div className="flex items-center justify-center mb-6">
            <div className="relative">
              <div className="w-20 h-20 bg-primary/10 rounded-2xl flex items-center justify-center">
                <Calendar className="w-10 h-10 text-primary" />
              </div>
              <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-amber-100 border-2 border-white rounded-full flex items-center justify-center">
                <Lock className="w-4 h-4 text-amber-600" />
              </div>
            </div>
          </div>

          {/* Heading */}
          <h1 className="text-2xl font-heading font-bold text-foreground text-center mb-2">
            Doctor Appointments
          </h1>
          <p className="text-muted-foreground text-center text-sm mb-8 leading-relaxed">
            Manage up to 3 upcoming doctor appointments and receive automatic email reminders 7 days, 1 day, and 2 hours before each visit — so you always arrive prepared.
          </p>

          {/* Features list */}
          <div className="bg-white border border-border rounded-xl p-5 mb-6 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Premium includes:</p>
            {[
              'Save up to 3 upcoming appointments',
              'Email reminder 7 days before',
              'Email reminder 1 day before',
              'Email reminder 2 hours before',
              'Direct link to generate consultation summary',
            ]?.map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="text-sm text-foreground">{item}</span>
              </div>
            ))}
          </div>

          {/* Option 1: Upgrade */}
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/subscription-management')}
            className="w-full flex items-center justify-between px-5 py-4 bg-primary text-primary-foreground rounded-xl font-semibold text-sm mb-3 hover:bg-primary/90 transition-colors shadow-lg">
            <div className="flex items-center gap-3">
              <Star className="w-5 h-5" />
              <div className="text-left">
                <p className="font-bold">Upgrade to Premium</p>
                <p className="text-xs text-primary-foreground/80">£4.99/month · Cancel anytime</p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5" />
          </motion.button>

          {/* Divider */}
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground font-medium">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Option 2: Refer friends */}
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/referral-program')}
            className="w-full flex items-center justify-between px-5 py-4 bg-white border-2 border-primary/30 text-foreground rounded-xl font-semibold text-sm hover:border-primary/60 hover:bg-primary/5 transition-colors">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-primary" />
              <div className="text-left">
                <p className="font-bold text-foreground">Refer 10 friends</p>
                <p className="text-xs text-muted-foreground">Unlock all Premium features for free</p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-primary" />
          </motion.button>

          <p className="text-xs text-muted-foreground text-center mt-5 leading-relaxed">
            Already referred 10 friends? Your Premium access will be activated automatically.
          </p>
        </motion.div>
      </div>
      <BottomTabNavigation />
    </div>
  );
};

export default AppointmentsUpgradePrompt;
